import React, {
    useContext,
    useEffect,
    useLayoutEffect,
    useReducer,
    useRef,
    useState,
} from "react";
import { Dialog, Heading, Modal } from "react-aria-components";
import { t } from "ttag";

import { ReactivatedContext } from "../../context";
import { Themed } from "../../styles/themes";
import { Confirmation } from "./Confirmation/Confirmation";
import { Hub } from "./Hub/Hub";
import { InitialLanding } from "./InitialLanding/InitialLanding";
import { Loading } from "./Loading/Loading";
import { MiniCart } from "./MiniCart/MiniCart";
import { ModalHeader } from "./ModalHeader/ModalHeader";
import { OfferDetailsModal } from "./OfferDetailsModal/OfferDetailsModal";
import { PDPDetailsModal } from "./PDPDetailsModal/PDPDetailsModal";
import { PopulatedLanding } from "./PopulatedLanding/PopulatedLanding";
import { useProductContext } from "./ProductContext/ProductContext";
import { Screen } from "./Screen/Screen";
import { useSuiteState } from "./SuiteState/SuiteState";
import { DEFAULT_SUITE } from "./SuiteState/persistence";
import { AppState, SKU, SuiteState, reduce } from "./engine";

import styles from "./SuiteBuilder.module.scss";

/**
 * The builder reads from `ProductContext` (catalog) and `SuiteState`
 * (persistent suite). `SuiteStateProvider` is mounted globally in
 * `client/index.tsx` / `client/renderer.tsx` so its value survives
 * SuiteBuilder mount/unmount cycles; `ProductContextProvider` mounts
 * in `SuiteBuilderPageTemplate`. Stories use the Mock variants.
 *
 * `initialState` seeds the reducer at mount and bypasses storage
 * entirely — story/test use only. Production never passes it.
 */
export const SuiteBuilder = ({ initialState }: { initialState?: AppState }) => {
    const { STATIC_URL } = useContext(ReactivatedContext);
    const {
        value: persistedSuite,
        set: persistSuite,
        loaded,
    } = useSuiteState();
    const { fetchState, retry } = useProductContext();
    const isControlled = initialState !== undefined;

    const [state, dispatch] = useReducer(
        reduce,
        undefined,
        (): AppState =>
            initialState ?? {
                view: "landing",
                // Hydration arrives via HYDRATE_SUITE; persistedSuite is
                // always DEFAULT_SUITE at this point.
                suite: DEFAULT_SUITE,
                walk: null,
            },
    );
    const [miniCartOpen, setMiniCartOpen] = useState(false);
    const [offerDetailsOpen, setOfferDetailsOpen] = useState(false);
    const showOfferDetails = () => setOfferDetailsOpen(true);
    const [detailsTarget, setDetailsTarget] = useState<{
        sku: SKU;
        // Commits the option (advances the walk, same as tapping its tile).
        onAdd: () => void;
        addLabel: string;
    } | null>(null);

    const hasContributions = state.suite.contributions.length > 0;

    // Single effect to avoid a first-hydration race: with separate
    // write-back and hydrate effects, write-back would persist
    // DEFAULT_SUITE before hydrate could dispatch. The ref distinguishes
    // external updates (pull into reducer) from local mutations
    // (push to storage). Initialized to DEFAULT_SUITE rather than the
    // current `persistedSuite` so that a SuiteBuilder remount against a
    // long-lived SuiteStateProvider (e.g. after navigating away and
    // back, or after HMR) correctly hydrates from persisted state
    // instead of overwriting it with the reducer's just-initialized
    // empty state.
    const lastSeenPersistedRef = useRef<SuiteState>(DEFAULT_SUITE);

    // useLayoutEffect (not useEffect) so the HYDRATE_SUITE dispatch
    // and its resulting render commit before the browser paints.
    useLayoutEffect(() => {
        if (isControlled) return;
        if (persistedSuite !== lastSeenPersistedRef.current) {
            lastSeenPersistedRef.current = persistedSuite;
            if (state.suite !== persistedSuite) {
                dispatch({ type: "HYDRATE_SUITE", suite: persistedSuite });
            }
            return;
        }
        if (state.suite !== persistedSuite) {
            persistSuite(state.suite);
        }
    }, [state.suite, persistedSuite, persistSuite, isControlled]);

    // Reset transient mini-cart state when the top-level view changes,
    // so reopening a walk or hub later doesn't pop the cart back open.
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- resets transient mini-cart UI state on top-level view change
        setMiniCartOpen(false);
    }, [state.view]);

    const isWalkOrHub = state.view === "walk" || state.view === "hub";

    // While storage hydrates, the underlying landing falls through to
    // InitialLanding (`loaded && hasContributions` is false). The
    // Loading overlay covers that frame.
    const showPopulated = loaded && hasContributions;
    const overlayVisible = !loaded || fetchState === "loading";
    const overlayErrored = fetchState === "error";

    return (
        <>
            <Themed
                theme={showPopulated ? "darkThemeText" : "eggplantGradient"}
                className={
                    showPopulated ? styles.landingPopulated : styles.landing
                }
                style={
                    showPopulated
                        ? {
                              backgroundImage: `url(${STATIC_URL}suite-builder/populated-landing/populated-background.webp)`,
                          }
                        : undefined
                }
                aria-busy={overlayVisible || overlayErrored}
            >
                <Loading
                    visible={overlayVisible}
                    errored={overlayErrored}
                    onRetry={retry}
                />
                {showPopulated ? (
                    <PopulatedLanding
                        state={state}
                        dispatch={dispatch}
                        onShowOfferDetails={showOfferDetails}
                    />
                ) : (
                    <InitialLanding
                        dispatch={dispatch}
                        onShowOfferDetails={showOfferDetails}
                    />
                )}
            </Themed>

            <Modal
                className={styles.modal}
                isOpen={isWalkOrHub}
                onOpenChange={(open) => {
                    if (!open) dispatch({ type: "CLOSE_BUILDER" });
                }}
            >
                <Dialog className={styles.dialog}>
                    <Heading slot="title" className={styles.visuallyHidden}>
                        {t`Suite Builder`}
                    </Heading>
                    <Themed theme="eggplantGradient" className={styles.surface}>
                        <ModalHeader
                            state={state}
                            onOpenMiniCart={() => setMiniCartOpen(true)}
                            onClose={() => dispatch({ type: "CLOSE_BUILDER" })}
                        />
                        {state.view === "walk" &&
                            (state.walk.confirmingContributionId ? (
                                <Confirmation
                                    state={state}
                                    dispatch={dispatch}
                                    onShowOfferDetails={showOfferDetails}
                                />
                            ) : (
                                <Screen
                                    state={state}
                                    dispatch={dispatch}
                                    onShowOfferDetails={showOfferDetails}
                                    onShowProductDetails={(
                                        sku,
                                        onAdd,
                                        addLabel,
                                    ) =>
                                        setDetailsTarget({
                                            sku,
                                            onAdd,
                                            addLabel,
                                        })
                                    }
                                />
                            ))}
                        {state.view === "hub" && (
                            <Hub
                                state={state}
                                dispatch={dispatch}
                                onShowOfferDetails={showOfferDetails}
                            />
                        )}
                    </Themed>
                </Dialog>
            </Modal>

            <MiniCart
                state={state}
                dispatch={dispatch}
                isOpen={miniCartOpen}
                onClose={() => setMiniCartOpen(false)}
                onShowOfferDetails={showOfferDetails}
            />

            <OfferDetailsModal
                isOpen={offerDetailsOpen}
                onClose={() => setOfferDetailsOpen(false)}
            />

            <PDPDetailsModal
                sku={detailsTarget?.sku ?? null}
                addLabel={detailsTarget?.addLabel ?? ""}
                onClose={() => setDetailsTarget(null)}
                onAdd={() => {
                    detailsTarget?.onAdd();
                    setDetailsTarget(null);
                }}
            />
        </>
    );
};
